import React from "react";
import { registerExtension, EXTENSION_SORT_ORDER } from "@core/components/PipelineComponents/ExtensionProvider";
import { TooltipWrapper } from "@core/components/TooltipWrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-light-svg-icons";
import { Switch } from "@core/components/ui/switch";
import { Label } from "@core/components/ui/label";
import { useApi } from "@core/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider, Controller } from "react-hook-form";

const CatalogComponent = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  const pluginName = "Ecommerce";
  const ecommerceSettingsGroup = "EcommerceSettings";
  const catalogSettingsGroup = "CatalogSettings";

  // Load EcommerceSettings (for CompareCartFilterEnabled)
  const { data: ecommerceSettings, isLoading: ecommerceLoading } = useQuery({
    queryKey: ["ecommerceSettings", pluginName, ecommerceSettingsGroup],
    queryFn: () => api["/settings/{plugin}/{group}"](pluginName, ecommerceSettingsGroup),
  });

  // Load CatalogSettings (for filter settings)
  const { data: catalogSettings, isLoading: catalogLoading } = useQuery({
    queryKey: ["catalogSettings", pluginName, catalogSettingsGroup],
    queryFn: () => api["/settings/{plugin}/{group}"](pluginName, catalogSettingsGroup),
  });

  const methods = useForm<any>({ 
    defaultValues: {
      CompareCartFilterEnabled: ecommerceSettings?.SettingValues?.CompareCartFilterEnabled ?? true,
      CategoryTreeEnabled: catalogSettings?.SettingValues?.CatalogFilters?.CategoryTreeEnabled ?? true,
      BrandNameEnabled: catalogSettings?.SettingValues?.CatalogFilters?.BrandNameEnabled ?? true,
      TagsEnabled: catalogSettings?.SettingValues?.CatalogFilters?.TagsEnabled ?? true,
      ProductAttributesEnabled: catalogSettings?.SettingValues?.CatalogFilters?.ProductAttributesEnabled ?? true,
    }
  });

  // Update form when settings load
  React.useEffect(() => {
    if (ecommerceSettings?.SettingValues && catalogSettings?.SettingValues) {
      methods.reset({
        CompareCartFilterEnabled: ecommerceSettings.SettingValues.CompareCartFilterEnabled ?? true,
        CategoryTreeEnabled: catalogSettings.SettingValues.CatalogFilters?.CategoryTreeEnabled ?? true,
        BrandNameEnabled: catalogSettings.SettingValues.CatalogFilters?.BrandNameEnabled ?? true,
        TagsEnabled: catalogSettings.SettingValues.CatalogFilters?.TagsEnabled ?? true,
        ProductAttributesEnabled: catalogSettings.SettingValues.CatalogFilters?.ProductAttributesEnabled ?? true,
      });
    }
  }, [ecommerceSettings, catalogSettings, methods]);

  // Mutation to save EcommerceSettings
  const saveEcommerceSettingsMutation = useMutation({
    mutationFn: async (data: any) => {
      await api["/settings/{plugin}/{group}/update"](pluginName, ecommerceSettingsGroup, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ecommerceSettings", pluginName, ecommerceSettingsGroup] });
    },
  });

  // Mutation to save CatalogSettings
  const saveCatalogSettingsMutation = useMutation({
    mutationFn: async (data: any) => {
      await api["/settings/{plugin}/{group}/update"](pluginName, catalogSettingsGroup, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["catalogSettings", pluginName, catalogSettingsGroup] });
    },
  });

  const handleCompareCartToggle = (checked: boolean) => {
    const currentValues = ecommerceSettings?.SettingValues ?? {};
    const updatedData = {
      ...currentValues,
      CompareCartFilterEnabled: checked,
    };
    saveEcommerceSettingsMutation.mutate(updatedData);
  };

  const handleFilterToggle = (filterName: string, checked: boolean) => {
    const currentValues = catalogSettings?.SettingValues ?? {};
    const currentFilters = currentValues.CatalogFilters ?? {};
    // Merge with all existing filter properties to ensure we don't lose any
    const updatedFilters = {
      ...currentFilters,
      [filterName]: checked,
    };
    const updatedData = {
      ...currentValues,
      CatalogFilters: updatedFilters,
    };
    saveCatalogSettingsMutation.mutate(updatedData);
  };

  if (ecommerceLoading || catalogLoading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Catalog</h3>
        <div className="text-sm text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  if (!ecommerceSettings || !catalogSettings) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Catalog</h3>
        <div className="text-sm text-muted-foreground">Settings not available.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Catalog</h3>
      <FormProvider {...methods}>
        <div className="space-y-4">
          {/* Enable Compare Cart Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="CompareCartFilterEnabled"
              control={methods.control}
              defaultValue={true}
              render={({ field }) => (
                <Switch
                  id="compareCart"
                  checked={field.value ?? true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleCompareCartToggle(checked);
                  }}
                  disabled={saveEcommerceSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="compareCart" className="cursor-pointer">
                Enable compare cart
              </Label>
              <TooltipWrapper
                tooltipContent="Allow users to compare multiple products side-by-side to help make purchasing decisions"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* Enable Category Filters Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="CategoryTreeEnabled"
              control={methods.control}
              defaultValue={true}
              render={({ field }) => (
                <Switch
                  id="categoryFilters"
                  checked={field.value ?? true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleFilterToggle("CategoryTreeEnabled", checked);
                  }}
                  disabled={saveCatalogSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="categoryFilters" className="cursor-pointer">
                Enable Category Filters
              </Label>
              <TooltipWrapper
                tooltipContent="Allow users to filter products by category using a hierarchical category tree"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* Enable Brand Filter Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="BrandNameEnabled"
              control={methods.control}
              defaultValue={true}
              render={({ field }) => (
                <Switch
                  id="brandFilter"
                  checked={field.value ?? true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleFilterToggle("BrandNameEnabled", checked);
                  }}
                  disabled={saveCatalogSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="brandFilter" className="cursor-pointer">
                Enable Brand Filter
              </Label>
              <TooltipWrapper
                tooltipContent="Allow users to filter products by brand or manufacturer name"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* Enable Tag Filter Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="TagsEnabled"
              control={methods.control}
              defaultValue={true}
              render={({ field }) => (
                <Switch
                  id="tagFilter"
                  checked={field.value ?? true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleFilterToggle("TagsEnabled", checked);
                  }}
                  disabled={saveCatalogSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="tagFilter" className="cursor-pointer">
                Enable Tag Filter
              </Label>
              <TooltipWrapper
                tooltipContent="Allow users to filter products by tags or labels assigned to products"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>

          {/* Enable Attribute Filter Toggle */}
          <div className="flex items-center space-x-2">
            <Controller
              name="ProductAttributesEnabled"
              control={methods.control}
              defaultValue={true}
              render={({ field }) => (
                <Switch
                  id="attributeFilter"
                  checked={field.value ?? true}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    handleFilterToggle("ProductAttributesEnabled", checked);
                  }}
                  disabled={saveCatalogSettingsMutation.isPending}
                />
              )}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="attributeFilter" className="cursor-pointer">
                Enable Attribute Filter
              </Label>
              <TooltipWrapper
                tooltipContent="Allow users to filter products by custom attributes like color, size, material, and other product-specific properties"
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted-foreground cursor-help"
                />
              </TooltipWrapper>
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

registerExtension(
  "admin.system.sitesetup.siteconfiguration",
  CatalogComponent,
  "Catalog",
  false,
  EXTENSION_SORT_ORDER.HIGH + 175  // Display after Products (HIGH + 150)
);
